/**
 * Federal Fiscal Year Utilities
 *
 * The US federal fiscal year runs from October 1 to September 30.
 * For example, FY2024 runs from October 1, 2023 to September 30, 2024.
 */

/**
 * Get the federal fiscal year for a given date.
 * If the month is October (9) or later, the fiscal year is the next calendar year.
 * Otherwise, the fiscal year is the current calendar year.
 */
export function getFiscalYear(date: Date): number {
  const month = date.getMonth(); // 0-indexed (0 = January, 9 = October)
  const year = date.getFullYear();

  // If October (9) or later, fiscal year is next calendar year
  if (month >= 9) {
    return year + 1;
  }
  return year;
}

/**
 * Get the current federal fiscal year.
 */
export function getCurrentFiscalYear(): number {
  return getFiscalYear(new Date());
}

/**
 * Get the start date of a federal fiscal year (October 1 of the prior calendar year).
 */
export function getFiscalYearStartDate(fiscalYear: number): Date {
  return new Date(fiscalYear - 1, 9, 1); // October 1 of prior year
}

/**
 * Get the end date of a federal fiscal year (September 30 at 23:59:59.999).
 */
export function getFiscalYearEndDate(fiscalYear: number): Date {
  return new Date(fiscalYear, 8, 30, 23, 59, 59, 999); // September 30 of same year
}

/**
 * Check if a date falls within a specific federal fiscal year.
 */
export function isInFiscalYear(date: Date, fiscalYear: number): boolean {
  return getFiscalYear(date) === fiscalYear;
}

/**
 * Check if a date falls within the current federal fiscal year.
 */
export function isInCurrentFiscalYear(date: Date): boolean {
  return isInFiscalYear(date, getCurrentFiscalYear());
}

/**
 * Get the Stockage Allowance reference date for an IAR run.
 *
 * Per policy the Dollar Value of Stockage Allowance must be calculated
 * using the **prior October 31** relative to the IAR run date:
 *   - If the run date is November 1 or later  → October 31 of the same year.
 *   - If the run date is October 31 or earlier → October 31 of the previous year.
 */
export function getStockageAllowanceDate(runDate: Date): Date {
  const month = runDate.getMonth(); // 0-indexed (9 = October, 10 = November)
  const year = runDate.getFullYear();

  // November (10) or later → use Oct 31 of the same calendar year
  if (month >= 10) {
    return new Date(year, 9, 31);
  }

  // October 31 or earlier → use Oct 31 of the previous calendar year
  return new Date(year - 1, 9, 31);
}

/**
 * Format a Stockage Allowance date as a human-readable label.
 * Returns e.g. "October 31, 2025".
 */
export function formatStockageAllowanceDate(date: Date): string {
  return date.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}
